import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatINR, formatDate, STATUS_TONE } from "@/lib/format";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  TrendingUp, AlertTriangle, Layers, Wallet, Receipt, CalendarClock,
  CalendarDays, Calendar, ArrowRight, Building2, Activity, Banknote, Clock,
  ChevronRight, Sparkles, Filter, X
} from "lucide-react";

const fmtCompact = (n) => {
  if (n == null) return "₹0";
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};

const HeroKpi = ({ tone, label, value, sub, icon: Icon, testid, to }) => {
  const card = (
    <div className={`${tone} p-5 rounded-sm relative overflow-hidden grain card-elev ${to ? "hover:shadow-lg transition-shadow cursor-pointer" : ""}`} data-testid={testid}>
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] opacity-80">{label}</div>
          <Icon className="w-4 h-4 opacity-70" />
        </div>
        <div className="font-display font-bold text-3xl mt-2.5 tracking-tight">{value}</div>
        {sub && <div className="text-[11px] opacity-80 mt-1">{sub}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
};

const Stat = ({ label, value, sub, icon: Icon, accent = "" }) => (
  <div className="card-surface p-4 rounded-sm">
    <div className="flex items-center justify-between">
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      {Icon && <Icon className={`w-4 h-4 ${accent || "text-slate-400"}`} />}
    </div>
    <div className="font-display font-bold text-2xl mt-2 text-slate-900">{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

const SectionHeader = ({ kicker, title, action }) => (
  <div className="flex items-end justify-between mb-3">
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-indigo-500/80">{kicker}</div>
      <h3 className="font-display text-xl font-bold tracking-tight text-slate-900 mt-0.5">{title}</h3>
    </div>
    {action}
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ fy: [], project: [], party_client_name: [] });
  const [fy, setFy] = useState("all");
  const [project, setProject] = useState("all");
  const [client, setClient] = useState("all");

  useEffect(() => {
    api.get("/dashboard/filters").then(({ data }) => setFilters(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (fy !== "all") params.fy = fy;
    if (project !== "all") params.project = project;
    if (client !== "all") params.party_client_name = client;
    setData(null);
    api.get("/dashboard/summary", { params }).then(({ data }) => setData(data));
  }, [fy, project, client]);

  const resetFilters = () => { setFy("all"); setProject("all"); setClient("all"); };
  const hasFilter = fy !== "all" || project !== "all" || client !== "all";

  // Upload Summary local filters (Project + Service Period)
  const [usProject, setUsProject] = useState("all");
  const [usPeriod, setUsPeriod] = useState("all");

  // Coming Soon · Next 3 Days — click-through invoice modal
  const [comingSoonModal, setComingSoonModal] = useState(null); // { label, date, invoices, amount, count } | null

  if (!data) {
    return (
      <div className="p-4 sm:p-6 lg:p-12 max-w-[1500px]">
        <div className="animate-pulse">
          <div className="h-10 w-72 bg-slate-200 rounded mb-6"></div>
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[1,2,3,4].map(i => <div key={i} className="h-28 bg-slate-200 rounded"></div>)}
          </div>
        </div>
      </div>
    );
  }

  const { kpis, status_counts = {}, coming_soon, ageing, breakdown = [], retention_heads = [], pending_by_head = [], top_clients = [], upload_summary = [], retention_by_client_type_head = [], hr_compliance = null } = data;
  const total3day = (coming_soon?.today?.amount || 0) + (coming_soon?.tomorrow?.amount || 0) + (coming_soon?.day_after?.amount || 0);
  const count3day = (coming_soon?.today?.count || 0) + (coming_soon?.tomorrow?.count || 0) + (coming_soon?.day_after?.count || 0);

  const breakdownTotals = (() => {
    const t = { invoice_count: 0, outstanding: 0, tds: 0, cess: 0, ld: 0, pending_retention: 0 };
    const headTotals = {};
    retention_heads.forEach((h) => { headTotals[h] = 0; });
    breakdown.forEach((r) => {
      t.invoice_count += Number(r.invoice_count) || 0;
      t.outstanding += Number(r.outstanding) || 0;
      t.tds += Number(r.tds) || 0;
      t.cess += Number(r.cess) || 0;
      t.ld += Number(r.ld) || 0;
      t.pending_retention += Number(r.pending_retention) || 0;
      retention_heads.forEach((h) => { headTotals[h] += Number(r[h]) || 0; });
    });
    return { ...t, heads: headTotals };
  })();

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1500px]">
      <div className="flex items-end justify-end flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-2">
          <span className="chip-soft info"><Sparkles className="w-3 h-3" /> Live</span>
          <span className="chip-soft">Total Invoices · {kpis.total_invoices}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="card-surface p-4 mb-8 flex flex-wrap items-end gap-3 rounded-sm" data-testid="dashboard-filters">
        <div className="flex items-center gap-2 text-slate-500 mr-2 pb-1.5">
          <Filter className="w-4 h-4" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em]">Filters</span>
        </div>
        <div className="w-48">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Financial Year</div>
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger className="rounded-none h-9" data-testid="dash-filter-fy"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All FY</SelectItem>
              {filters.fy.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-64">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Project</div>
          <Select value={project} onValueChange={setProject}>
            <SelectTrigger className="rounded-none h-9" data-testid="dash-filter-project"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {filters.project.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Client / Party</div>
          <Select value={client} onValueChange={setClient}>
            <SelectTrigger className="rounded-none h-9" data-testid="dash-filter-client"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {(filters.party_client_name || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {hasFilter && (
          <button
            onClick={resetFilters}
            className="text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-900 flex items-center gap-1 h-9 px-2"
            data-testid="dash-filter-reset"
          >
            <X className="w-3.5 h-3.5" /> Reset
          </button>
        )}
        {hasFilter && (
          <div className="ml-auto text-[11px] text-slate-400 pb-1.5">
            {fy !== "all" && <span className="chip-soft info mr-1">FY · {fy}</span>}
            {project !== "all" && <span className="chip-soft info mr-1">Project · {project}</span>}
            {client !== "all" && <span className="chip-soft info">Client · {client}</span>}
          </div>
        )}
      </div>

      {/* Hero KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <HeroKpi tone="kpi-gradient-1" testid="kpi-outstanding" label="Outstanding" value={fmtCompact(kpis.total_outstanding)} sub={`${formatINR(kpis.total_outstanding)} · click to view`} icon={Wallet} to={`/invoices?outstanding=1${fy !== "all" ? `&fy=${fy}` : ""}${project !== "all" ? `&project=${project}` : ""}${client !== "all" ? `&party_client_name=${encodeURIComponent(client)}` : ""}`} />
        <HeroKpi tone="kpi-gradient-2" testid="kpi-coming-3day" label="Coming Soon · Next 3 Days" value={fmtCompact(total3day)} sub={`${count3day} invoice${count3day === 1 ? "" : "s"}`} icon={CalendarClock} />
        <HeroKpi tone="kpi-gradient-4" testid="kpi-overdue" label="Overdue" value={fmtCompact(kpis.overdue_amount)} sub={`${kpis.overdue_count} invoices past due · click to view`} icon={AlertTriangle} to={`/invoices?overdue=1${fy !== "all" ? `&fy=${fy}` : ""}${project !== "all" ? `&project=${project}` : ""}${client !== "all" ? `&party_client_name=${encodeURIComponent(client)}` : ""}`} />
        <HeroKpi tone="kpi-gradient-3" testid="kpi-paid" label="Collected to Date" value={fmtCompact(kpis.total_paid)} sub={`${formatINR(kpis.total_paid)}`} icon={Banknote} />
      </div>

      {/* Secondary stat row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
        <Stat label="Pending Retention" value={fmtCompact(kpis.total_pending_retention)} sub={`Total held ${fmtCompact(kpis.total_retention)}`} icon={Layers} accent="text-indigo-500" />
        <Stat label="Release Req · Pending" value={kpis.release_requests_pending} sub={fmtCompact(kpis.release_requests_pending_amount)} icon={Receipt} accent="text-indigo-500" />
        <Stat label="TDS Withheld" value={fmtCompact(kpis.total_tds)} icon={TrendingUp} accent="text-slate-500" />
        <Stat label="CESS" value={fmtCompact(kpis.total_cess)} icon={TrendingUp} accent="text-slate-500" />
        <Stat label="LD Applied" value={fmtCompact(kpis.total_ld)} icon={AlertTriangle} accent="text-amber-500" />
        <Stat label="Net Payable" value={fmtCompact(kpis.total_net_payable)} icon={Wallet} accent="text-indigo-500" />
      </div>

      {/* Coming Soon — 3 day deadline cards (figures only; click to view invoices) */}
      <SectionHeader
        kicker="Cash Inflow"
        title="Coming Soon · Next 3 Days"
        action={<span className="text-xs text-slate-500">Based on Payment Due Date · Click any card to view invoices</span>}
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        {[
          { key: "today", node: coming_soon.today, className: "today", icon: CalendarDays },
          { key: "tomorrow", node: coming_soon.tomorrow, className: "tomorrow", icon: Calendar },
          { key: "day_after", node: coming_soon.day_after, className: "day-after", icon: Calendar },
        ].map(({ key, node, className, icon: Icon }) => {
          const clickable = node.count > 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => clickable && setComingSoonModal({ ...node })}
              disabled={!clickable}
              className={`deadline-card ${className} p-5 rounded-sm text-left w-full transition-all ${clickable ? "cursor-pointer hover:shadow-lg hover:-translate-y-[1px]" : "cursor-default opacity-90"}`}
              data-testid={`due-${key}`}
              aria-label={`${node.label} · ${node.count} invoice${node.count === 1 ? "" : "s"}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">{node.label}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{formatDate(node.date)}</div>
                </div>
                <Icon className="w-5 h-5 text-indigo-500/70" />
              </div>
              <div className="font-display font-bold text-3xl text-slate-900 mt-3 tracking-tight">{fmtCompact(node.amount)}</div>
              <div className="text-xs text-slate-500 mt-1">
                {node.count === 0
                  ? "No invoices"
                  : <>{node.count} invoice{node.count === 1 ? "" : "s"} · {formatINR(node.amount)}</>}
              </div>
              {clickable && (
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-600 mt-3 flex items-center gap-1" data-testid={`due-${key}-cta`}>
                  View invoices <ArrowRight className="w-3 h-3" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Coming Soon · invoice list modal */}
      <Dialog open={!!comingSoonModal} onOpenChange={(o) => { if (!o) setComingSoonModal(null); }}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center justify-between gap-3">
              <span>{comingSoonModal?.label} · {comingSoonModal && formatDate(comingSoonModal.date)}</span>
              <span className="font-mono-data text-base font-bold text-slate-900">{comingSoonModal && formatINR(comingSoonModal.amount)}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="text-[11px] text-slate-500 uppercase tracking-[0.2em] font-semibold mb-1">
            {comingSoonModal?.count} invoice{(comingSoonModal?.count || 0) === 1 ? "" : "s"} due
          </div>
          <div className="border border-slate-200 overflow-auto" style={{ maxHeight: "60vh" }} data-testid="coming-soon-modal-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Invoice #</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Client</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {(comingSoonModal?.invoices || []).map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`coming-soon-row-${inv.invoice_number}`}>
                    <td className="px-3 py-2">
                      <Link
                        to={`/invoices/${inv.id}`}
                        onClick={() => setComingSoonModal(null)}
                        className="font-mono-data text-indigo-700 hover:text-indigo-900 hover:underline"
                      >
                        {inv.invoice_number}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-slate-700 text-xs">{inv.party_client_name || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data font-semibold text-slate-900">{formatINR(inv.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
              {(comingSoonModal?.invoices?.length || 0) > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-indigo-300 bg-indigo-50">
                    <td colSpan={2} className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-900">Total</td>
                    <td className="px-3 py-2 text-right font-mono-data font-bold text-indigo-900">{comingSoonModal && formatINR(comingSoonModal.amount)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ageing buckets */}
      <SectionHeader kicker="Outstanding Ageing" title="Receivables by Age" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
        {Object.entries(ageing).map(([key, bucket]) => {
          const tone =
            key === "not_due" ? "border-l-4 border-l-emerald-500" :
            key === "0_30" ? "border-l-4 border-l-amber-500" :
            key === "31_60" ? "border-l-4 border-l-orange-500" :
            key === "61_90" ? "border-l-4 border-l-rose-500" :
            key === "90_plus" ? "border-l-4 border-l-red-600" :
            "border-l-4 border-l-slate-300";
          return (
            <div key={key} className={`card-surface ${tone} p-4 rounded-sm`} data-testid={`age-${key}`}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{bucket.label}</div>
              <div className="font-display font-bold text-xl text-slate-900 mt-2">{fmtCompact(bucket.amount)}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{bucket.count} invoice{bucket.count === 1 ? "" : "s"}</div>
            </div>
          );
        })}
      </div>

      {/* FY × Project × Client breakdown */}
      <SectionHeader
        kicker="Receivables Matrix"
        title="Pending by FY · Project · Client"
        action={<span className="text-xs text-slate-500">{breakdown.length} group{breakdown.length === 1 ? "" : "s"} · scroll for more · click a row to drill down</span>}
      />
      <div className="card-surface rounded-sm mb-10" data-testid="breakdown-table">
        <div className="overflow-auto" style={{ maxHeight: "480px" }}>
        <table className="w-full text-sm border-collapse">
          <thead className="bg-slate-100 sticky top-0 z-10">
            <tr className="border-b-2 border-slate-400">
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">FY</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Project</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Client</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Inv</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Outstanding</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">TDS</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">CESS</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">LD</th>
              {retention_heads.map((h) => (
                <th key={h} className="text-right px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-indigo-800 whitespace-nowrap bg-slate-100 border border-slate-300">{h}</th>
              ))}
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Pend. Retn.</th>
            </tr>
            {breakdown.length > 0 && (
              <tr className="border-b-2 border-indigo-300" data-testid="breakdown-totals">
                <th colSpan={3} className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-900 bg-indigo-50 border border-indigo-200">Total · All Groups</th>
                <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-indigo-900 bg-indigo-50 border border-indigo-200">{breakdownTotals.invoice_count}</th>
                <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-amber-700 bg-indigo-50 border border-indigo-200">{formatINR(breakdownTotals.outstanding)}</th>
                <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-slate-800 bg-indigo-50 border border-indigo-200">{formatINR(breakdownTotals.tds)}</th>
                <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-slate-800 bg-indigo-50 border border-indigo-200">{formatINR(breakdownTotals.cess)}</th>
                <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-slate-800 bg-indigo-50 border border-indigo-200">{formatINR(breakdownTotals.ld)}</th>
                {retention_heads.map((h) => (
                  <th key={h} className={`text-right px-3 py-2.5 font-mono-data text-xs font-bold bg-indigo-50 border border-indigo-200 ${breakdownTotals.heads[h] > 0 ? "text-indigo-800" : "text-slate-400"}`}>
                    {breakdownTotals.heads[h] > 0 ? formatINR(breakdownTotals.heads[h]) : "—"}
                  </th>
                ))}
                <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-indigo-900 bg-indigo-50 border border-indigo-200">{formatINR(breakdownTotals.pending_retention)}</th>
              </tr>
            )}
          </thead>
          <tbody>
            {breakdown.length === 0 && (
              <tr><td colSpan={9 + retention_heads.length} className="text-center text-sm text-slate-400 py-10">All caught up · no pending receivables.</td></tr>
            )}
            {breakdown.map((row, idx) => {
              const linkParams = new URLSearchParams();
              if (row.fy) linkParams.set("fy", row.fy);
              if (row.project) linkParams.set("project", row.project);
              if (row.client) linkParams.set("party_client_name", row.client);
              const drillTo = `/invoices?${linkParams.toString()}`;
              return (
                <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50/70 cursor-pointer" data-testid={`breakdown-row-${idx}`} onClick={() => { window.location.href = drillTo; }}>
                  <td className="px-4 py-3 font-medium text-slate-900 border border-slate-200">{row.fy}</td>
                  <td className="px-4 py-3 text-slate-700 border border-slate-200">{row.project}</td>
                  <td className="px-4 py-3 text-slate-700 font-medium border border-slate-200">
                    <Link to={drillTo} className="hover:text-indigo-700 inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {row.client} <ArrowRight className="w-3 h-3 opacity-50" />
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700 border border-slate-200">{row.invoice_count}</td>
                  <td className="px-4 py-3 text-right font-mono-data font-semibold text-amber-700 border border-slate-200">{formatINR(row.outstanding)}</td>
                  <td className="px-4 py-3 text-right font-mono-data text-slate-700 border border-slate-200">{formatINR(row.tds)}</td>
                  <td className="px-4 py-3 text-right font-mono-data text-slate-700 border border-slate-200">{formatINR(row.cess)}</td>
                  <td className="px-4 py-3 text-right font-mono-data text-slate-700 border border-slate-200">{formatINR(row.ld)}</td>
                  {retention_heads.map((h) => (
                    <td key={h} className={`px-3 py-3 text-right font-mono-data border border-slate-200 ${row[h] > 0 ? "text-indigo-700 font-semibold" : "text-slate-300"}`}>
                      {row[h] > 0 ? formatINR(row[h]) : "—"}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right font-mono-data font-semibold text-indigo-700 border border-slate-200">{formatINR(row.pending_retention)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Three columns: Pending by head | Top clients | Status mix */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
        <div className="card-surface rounded-sm p-5" data-testid="head-pending">
          <SectionHeader kicker="Retention Heads" title="Pending by Head" />
          <div className="space-y-2 mt-2">
            {pending_by_head.every(h => h.amount === 0) && (
              <div className="text-xs text-slate-400 py-6 text-center">No pending retention.</div>
            )}
            {pending_by_head.filter(h => h.amount > 0).map((h) => {
              const maxAmt = Math.max(...pending_by_head.map(x => x.amount), 1);
              const pct = (h.amount / maxAmt) * 100;
              return (
                <div key={h.head}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-slate-700">{h.head}</span>
                    <span className="font-mono-data text-slate-900">{formatINR(h.amount)}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{h.count} hold{h.count === 1 ? "" : "s"}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card-surface rounded-sm p-5" data-testid="top-clients">
          <SectionHeader kicker="Top Clients" title="By Outstanding" />
          <div className="space-y-3 mt-2">
            {top_clients.length === 0 && <div className="text-xs text-slate-400 py-6 text-center">No data.</div>}
            {top_clients.map((c, i) => (
              <Link key={c.client} to={`/invoices?party_client_name=${encodeURIComponent(c.client)}${fy !== "all" ? `&fy=${fy}` : ""}${project !== "all" ? `&project=${project}` : ""}`} className="flex items-center gap-3 hover:bg-slate-50 rounded p-1 -mx-1 transition-colors" data-testid={`top-client-${i}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white shadow-sm ${
                  ["bg-indigo-600", "bg-violet-600", "bg-fuchsia-600", "bg-rose-600", "bg-amber-600"][i]
                }`}>
                  #{i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-slate-400" /> {c.client}
                    </div>
                    <div className="font-mono-data text-sm font-semibold text-slate-900">{fmtCompact(c.outstanding)}</div>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {c.invoice_count} invoice{c.invoice_count === 1 ? "" : "s"} · Retn. {fmtCompact(c.pending_retention)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="card-surface rounded-sm p-5" data-testid="status-mix">
          <SectionHeader kicker="Lifecycle" title="Invoice Status Mix" />
          <div className="space-y-1.5 mt-2">
            {Object.entries(status_counts).length === 0 && <div className="text-xs text-slate-400 py-6 text-center">No data.</div>}
            {Object.entries(status_counts)
              .sort(([, a], [, b]) => b - a)
              .map(([s, c]) => (
                <Link key={s} to={`/invoices?status=${encodeURIComponent(s)}${fy !== "all" ? `&fy=${fy}` : ""}${project !== "all" ? `&project=${project}` : ""}`} className="flex items-center justify-between border-b border-slate-100 py-1.5 last:border-b-0 hover:bg-slate-50 px-1 -mx-1 rounded transition-colors" data-testid={`status-link-${s}`}>
                  <span className={`status-pill ${STATUS_TONE(s)}`}>{s}</span>
                  <span className="font-mono-data text-sm font-semibold text-slate-900">{c}</span>
                </Link>
              ))}
          </div>
        </div>
      </div>

      {/* Upload Summary · Client × Project × Service Period × Invoice Count */}
      {(() => {
        const usProjects = Array.from(new Set(upload_summary.map((r) => r.project).filter((v) => v && v !== "—"))).sort();
        const usPeriods = Array.from(new Set(upload_summary.map((r) => r.service_period).filter((v) => v && v !== "—"))).sort();
        const usFiltered = upload_summary.filter((r) => {
          if (usProject !== "all" && r.project !== usProject) return false;
          if (usPeriod !== "all" && r.service_period !== usPeriod) return false;
          return true;
        });
        const usHasFilter = usProject !== "all" || usPeriod !== "all";
        const usTotals = {
          count: usFiltered.reduce((s, r) => s + (r.invoice_count || 0), 0),
          base: usFiltered.reduce((s, r) => s + (r.total_base || 0), 0),
          outstanding: usFiltered.reduce((s, r) => s + (r.total_outstanding || 0), 0),
        };
        return (<>
      <SectionHeader
        kicker="Upload Summary"
        title="Invoices Uploaded · by Client · Project · Service Period"
        action={
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-slate-500 hidden sm:inline" data-testid="upload-summary-count">
              {usFiltered.length} group{usFiltered.length === 1 ? "" : "s"} · {usTotals.count} invoices
              {usHasFilter && <span className="ml-1 text-indigo-600 font-semibold">(filtered)</span>}
            </span>
            <Select value={usProject} onValueChange={setUsProject}>
              <SelectTrigger className="h-8 w-44 rounded-none border-slate-300 text-xs" data-testid="upload-summary-project-filter">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {usProjects.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={usPeriod} onValueChange={setUsPeriod}>
              <SelectTrigger className="h-8 w-44 rounded-none border-slate-300 text-xs" data-testid="upload-summary-period-filter">
                <SelectValue placeholder="All periods" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Service Periods</SelectItem>
                {usPeriods.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            {usHasFilter && (
              <button
                onClick={() => { setUsProject("all"); setUsPeriod("all"); }}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-red-600 border border-slate-200 hover:border-red-300 rounded-none"
                data-testid="upload-summary-reset-filter"
              >
                <X className="w-3 h-3" /> Reset
              </button>
            )}
          </div>
        }
      />
      <div className="card-surface rounded-sm mb-10" data-testid="upload-summary-table">
        <div className="overflow-auto" style={{ maxHeight: "420px" }}>
          <table className="w-full text-sm border-collapse">
            <thead className="bg-slate-100 sticky top-0 z-10">
              <tr className="border-b-2 border-slate-400">
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Client / Party</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Project</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Service Period</th>
                <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">No. of Invoices</th>
                <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Base Amount</th>
                <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Outstanding</th>
              </tr>
              {usFiltered.length > 0 && (
                <tr className="border-b-2 border-indigo-300" data-testid="upload-summary-totals">
                  <th colSpan={3} className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-900 bg-indigo-50 border border-indigo-200">
                    Total{usHasFilter ? " · Filtered" : " · All Groups"}
                  </th>
                  <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-indigo-900 bg-indigo-50 border border-indigo-200">{usTotals.count}</th>
                  <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-slate-800 bg-indigo-50 border border-indigo-200">{formatINR(usTotals.base)}</th>
                  <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-amber-700 bg-indigo-50 border border-indigo-200">{formatINR(usTotals.outstanding)}</th>
                </tr>
              )}
            </thead>
            <tbody>
              {usFiltered.length === 0 && (
                <tr><td colSpan={6} className="text-center text-sm text-slate-400 py-10">
                  {upload_summary.length === 0 ? "No invoices uploaded yet." : "No groups match the selected filters."}
                </td></tr>
              )}
              {usFiltered.map((row, idx) => {
                const linkParams = new URLSearchParams();
                if (row.client && row.client !== "—") linkParams.set("party_client_name", row.client);
                if (row.project && row.project !== "—") linkParams.set("project", row.project);
                const drillTo = `/invoices?${linkParams.toString()}`;
                return (
                  <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50/70 cursor-pointer" data-testid={`upload-summary-row-${idx}`} onClick={() => { window.location.href = drillTo; }}>
                    <td className="px-4 py-3 font-medium text-slate-900 border border-slate-200">
                      <Link to={drillTo} className="hover:text-indigo-700 inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {row.client} <ArrowRight className="w-3 h-3 opacity-50" />
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-700 border border-slate-200">{row.project}</td>
                    <td className="px-4 py-3 text-slate-700 border border-slate-200">{row.service_period}</td>
                    <td className="px-4 py-3 text-right font-mono-data text-slate-900 font-bold border border-slate-200">{row.invoice_count}</td>
                    <td className="px-4 py-3 text-right font-mono-data text-slate-700 border border-slate-200">{formatINR(row.total_base)}</td>
                    <td className={`px-4 py-3 text-right font-mono-data border border-slate-200 ${row.total_outstanding > 0 ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{formatINR(row.total_outstanding)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
        </>);
      })()}

      {/* Retentions · Client × Type × Head — Raised vs Paid counts */}
      <SectionHeader
        kicker="Retentions"
        title="By Client · Type · Head — Raised vs Paid"
        action={
          <span className="text-xs text-slate-500 hidden sm:inline">
            {retention_by_client_type_head.length} group{retention_by_client_type_head.length === 1 ? "" : "s"} ·
            {" "}<strong className="text-slate-900">{retention_by_client_type_head.reduce((s, r) => s + (r.raised_count || 0), 0)}</strong> raised ·
            {" "}<strong className="text-emerald-700">{retention_by_client_type_head.reduce((s, r) => s + (r.paid_count || 0), 0)}</strong> paid
          </span>
        }
      />
      <div className="card-surface rounded-sm mb-10" data-testid="rcth-table">
        <div className="overflow-auto" style={{ maxHeight: "480px" }}>
          <table className="w-full text-sm border-collapse">
            <thead className="bg-slate-100 sticky top-0 z-10">
              <tr className="border-b-2 border-slate-400">
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Client / Party</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Type</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Head</th>
                <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">No. Raised</th>
                <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">No. Paid</th>
                <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Raised Amount</th>
                <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Paid Amount</th>
              </tr>
              {retention_by_client_type_head.length > 0 && (
                <tr className="border-b-2 border-indigo-300" data-testid="rcth-totals">
                  <th colSpan={3} className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-900 bg-indigo-50 border border-indigo-200">Total · All Groups</th>
                  <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-indigo-900 bg-indigo-50 border border-indigo-200">{retention_by_client_type_head.reduce((s, r) => s + (r.raised_count || 0), 0)}</th>
                  <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-emerald-800 bg-indigo-50 border border-indigo-200">{retention_by_client_type_head.reduce((s, r) => s + (r.paid_count || 0), 0)}</th>
                  <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-slate-800 bg-indigo-50 border border-indigo-200">{formatINR(retention_by_client_type_head.reduce((s, r) => s + (r.raised_amount || 0), 0))}</th>
                  <th className="text-right px-4 py-2.5 font-mono-data text-xs font-bold text-emerald-700 bg-indigo-50 border border-indigo-200">{formatINR(retention_by_client_type_head.reduce((s, r) => s + (r.paid_amount || 0), 0))}</th>
                </tr>
              )}
            </thead>
            <tbody>
              {retention_by_client_type_head.length === 0 && (
                <tr><td colSpan={7} className="text-center text-sm text-slate-400 py-10">No retention entries in scope.</td></tr>
              )}
              {retention_by_client_type_head.map((row, idx) => {
                const tonePill =
                  row.type === "Retention Paid On Request" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  row.type === "Retention Paid Without Request" ? "bg-teal-50 text-teal-700 border-teal-200" :
                  row.type === "Retention Request Raised" ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
                  "bg-amber-50 text-amber-700 border-amber-200";
                const linkParams = new URLSearchParams();
                if (row.client && row.client !== "—") linkParams.set("party_client_name", row.client);
                if (row.head) linkParams.set("head", row.head);
                if (row.type) linkParams.set("type", row.type);
                const drillTo = `/retentions?${linkParams.toString()}`;
                return (
                  <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50/70 cursor-pointer" data-testid={`rcth-row-${idx}`} onClick={() => { window.location.href = drillTo; }}>
                    <td className="px-4 py-3 font-medium text-slate-900 border border-slate-200">
                      <Link to={drillTo} className="hover:text-indigo-700 inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {row.client} <ArrowRight className="w-3 h-3 opacity-50" />
                      </Link>
                    </td>
                    <td className="px-3 py-3 border border-slate-200">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold border rounded ${tonePill}`}>{row.type}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 border border-slate-200">{row.head}</td>
                    <td className="px-4 py-3 text-right font-mono-data text-slate-900 font-bold border border-slate-200">{row.raised_count}</td>
                    <td className={`px-4 py-3 text-right font-mono-data font-bold border border-slate-200 ${row.paid_count > 0 ? "text-emerald-700" : "text-slate-400"}`}>{row.paid_count}</td>
                    <td className="px-4 py-3 text-right font-mono-data text-slate-700 border border-slate-200">{formatINR(row.raised_amount)}</td>
                    <td className={`px-4 py-3 text-right font-mono-data border border-slate-200 ${row.paid_amount > 0 ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>{formatINR(row.paid_amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* HR Compliance Snapshot */}
      {hr_compliance && (
        <>
          <SectionHeader
            kicker="HR Compliance"
            title={`FY ${hr_compliance.fy} — Live Snapshot`}
            action={
              <Link to="/hr-compliance" className="text-xs font-semibold text-[#002FA7] hover:underline inline-flex items-center gap-1">
                Open module <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            }
          />

          {/* Layer 1 + 2 — Stat cards strip */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4" data-testid="hr-dash-stats">
            <Link to="/hr-compliance" className="bg-white border border-slate-200 hover:border-[#002FA7] hover:shadow-md transition px-4 py-3 block">
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">Active RCs</div>
              <div className="text-3xl font-display font-bold text-slate-900 mt-1">{hr_compliance.active_rcs}</div>
              <div className="text-[10px] text-slate-500 mt-1">in your projects</div>
            </Link>
            <Link to="/hr-compliance" className="bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-md transition px-4 py-3 block">
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">Approval Rate</div>
              <div className="text-3xl font-display font-bold text-emerald-700 mt-1">{hr_compliance.approval_rate_pct}%</div>
              <div className="w-full bg-slate-100 h-1.5 rounded overflow-hidden mt-2">
                <div className="bg-emerald-500 h-full" style={{ width: `${Math.min(hr_compliance.approval_rate_pct, 100)}%` }} />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                {hr_compliance.months_approved}/{hr_compliance.effective_months_expected ?? hr_compliance.total_months_expected} months approved
                {(hr_compliance.months_no_work || 0) > 0 && (
                  <span className="ml-1 text-rose-600">· {hr_compliance.months_no_work} no-work excluded</span>
                )}
              </div>
            </Link>
            <Link to="/hr-compliance" className="bg-white border border-slate-200 hover:border-amber-400 hover:shadow-md transition px-4 py-3 block">
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">{hr_compliance.current_month} — Not Applied</div>
              <div className="text-3xl font-display font-bold text-amber-700 mt-1">{hr_compliance.not_applied_count}</div>
              <div className="text-[10px] text-slate-500 mt-1">
                RCs missing this month
                {(hr_compliance.no_work_current_count || 0) > 0 && (
                  <span className="ml-1 text-rose-600">· {hr_compliance.no_work_current_count} no-work</span>
                )}
              </div>
            </Link>
            <Link to="/hr-compliance" className="bg-white border border-slate-200 hover:border-amber-400 hover:shadow-md transition px-4 py-3 block">
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">{hr_compliance.current_month} — Awaiting Approval</div>
              <div className="text-3xl font-display font-bold text-amber-700 mt-1">{hr_compliance.applied_not_approved_count}</div>
              <div className="text-[10px] text-slate-500 mt-1">Applied, not yet approved</div>
            </Link>
            <Link to="/hr-compliance" className="bg-white border border-slate-200 hover:border-sky-400 hover:shadow-md transition px-4 py-3 block">
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">ID Card Gap</div>
              <div className="text-3xl font-display font-bold text-slate-900 mt-1">{hr_compliance.id_cards_gap}</div>
              <div className="text-[10px] text-slate-500 mt-1">{hr_compliance.id_cards_approved_total} / {hr_compliance.id_cards_applied_total} approved</div>
            </Link>
          </div>

          {/* Layer 3 — Snapshot table (all RCs, sorted by lowest approval count first) */}
          {hr_compliance.top_rcs?.length > 0 && (
            <div className="card-surface rounded-sm mb-10" data-testid="hr-dash-snapshot">
              <div className="px-3 py-2 bg-gradient-to-r from-sky-100 via-blue-100 to-indigo-100 border-b-2 border-indigo-300 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">RCs Needing Attention · All {hr_compliance.top_rcs.length} RC{hr_compliance.top_rcs.length === 1 ? "" : "s"}</span>
                  <span className="text-[10px] text-slate-500 ml-2">(sorted by lowest approval count first)</span>
                </div>
                <span className="text-[10px] text-slate-500">Scroll for more ↓</span>
              </div>
              <div className="overflow-auto" style={{ maxHeight: "480px" }}>
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-slate-100 sticky top-0 z-10">
                    <tr className="border-b border-slate-300">
                      <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100">RC No</th>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100">Description</th>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100">Project</th>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100">{hr_compliance.current_month}</th>
                      <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100">FY Applied / Approved</th>
                      <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100">12-Month Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hr_compliance.top_rcs.map((r) => {
                      const tone =
                        r.current_month_status === "no_work" ? "bg-rose-50 text-rose-700 border-rose-300" :
                        r.current_month_status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        r.current_month_status === "applied" ? "bg-amber-50 text-amber-700 border-amber-200" :
                        "bg-rose-50 text-rose-700 border-rose-200";
                      const label =
                        r.current_month_status === "no_work" ? "No Work" :
                        r.current_month_status === "approved" ? "Approved" :
                        r.current_month_status === "applied" ? "Applied" : "Pending";
                      return (
                        <tr key={r.id} className="border-b border-slate-200 hover:bg-sky-50/40 cursor-pointer" onClick={() => navigate("/hr-compliance")} data-testid={`hr-dash-row-${r.rc_no}`}>
                          <td className="px-3 py-2 font-mono-data font-semibold text-slate-900">{r.rc_no}</td>
                          <td className="px-3 py-2 text-slate-700 text-xs">{r.rc_description || "—"}</td>
                          <td className="px-3 py-2 text-slate-700 text-xs">{r.project}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded border ${tone}`}>{label}</span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono-data text-slate-900">
                            <span className="text-amber-700">{r.fy_applied_count}</span>
                            <span className="text-slate-400 mx-1">/</span>
                            <span className="text-emerald-700">{r.fy_approved_count}</span>
                            <span className="text-slate-400 text-[10px] ml-1">of 12</span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="grid grid-cols-12 gap-0.5">
                              {hr_compliance.months.map((m) => {
                                const st = r.fy_months?.[m] || {};
                                const t = st.no_work ? "bg-rose-500" : st.approval_status ? "bg-emerald-400" : st.compliance_applied ? "bg-amber-400" : "bg-slate-200";
                                const lbl = st.no_work ? "No Work" : st.approval_status ? "Approved" : st.compliance_applied ? "Applied" : "—";
                                return <div key={m} className={`h-3 ${t}`} title={`${m}: ${lbl}`} />;
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
